import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import Input from '../../../components/Input';
import Select from '../../../components/Select';
import { SUBORDINATE_LEVELS } from '../../../constants';

/**
 * Add/Edit Unit Modal Component with License selection
 */
function AddLicenseUnitModal({
  open,
  onClose,
  onConfirm,
  unitLevels = [],
  unitStatuses = [],
  verifyModes = [],
  availableLicenses = [],
  unitData = null,
  editMode = false,
}) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    level: '',
    location: '',
    subordinateLevelId: '',
    verifyModeIds: [],
    status: 2, // Default: Active
    licenseId: '',
  });

  // Get subordinate levels with translated names
  const subordinateLevelOptions = SUBORDINATE_LEVELS.map((level) => ({
    id: level.id,
    name: t(level.nameKey),
  }));

  // Get license options - include current unit's license if editing
  const getLicenseOptions = () => {
    const options = [...availableLicenses];
    // If editing and unit has a license, add it to the list
    if (editMode && unitData?.licenseId) {
      const existingLicense = options.find((l) => l.id === unitData.licenseId);
      if (!existingLicense) {
        options.unshift({
          id: unitData.licenseId,
          code: unitData.licenseCode || `License #${unitData.licenseId}`,
          description: '',
        });
      }
    }
    return options;
  };

  // Populate form data when in edit mode
  useEffect(() => {
    if (editMode && unitData) {
      setFormData({
        code: unitData.code || '',
        name: unitData.name || '',
        level: unitData.level !== undefined ? unitData.level : '',
        location: unitData.location || '',
        subordinateLevelId: unitData.subordinateLevelId !== undefined ? unitData.subordinateLevelId : '',
        verifyModeIds: unitData.verifyModeIds || [],
        status: unitData.status !== undefined ? unitData.status : 2,
        licenseId: unitData.licenseId || '',
      });
    } else {
      setFormData({
        code: '',
        name: '',
        level: '',
        location: '',
        subordinateLevelId: '',
        verifyModeIds: [],
        status: 2,
        licenseId: '',
      });
    }
  }, [editMode, unitData, open]);

  // Handle input change
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.code && formData.name && formData.level !== '';
  };

  // Handle confirm
  const handleConfirm = () => {
    const cleanedData = {
      ...formData,
      subordinateLevelId:
        formData.subordinateLevelId === '' || formData.subordinateLevelId === null
          ? null
          : parseInt(formData.subordinateLevelId, 10),
      level: typeof formData.level === 'string' ? parseInt(formData.level, 10) : formData.level,
      verifyModeIds: Array.isArray(formData.verifyModeIds) ? formData.verifyModeIds : [],
      status: typeof formData.status === 'string' ? parseInt(formData.status, 10) : formData.status,
      licenseId:
        formData.licenseId === '' || formData.licenseId === null
          ? null
          : parseInt(formData.licenseId, 10),
    };

    if (editMode && unitData) {
      onConfirm({ ...cleanedData, id: unitData.id });
    } else {
      onConfirm(cleanedData);
    }
    handleClose();
  };

  // Handle close and reset form
  const handleClose = () => {
    setFormData({
      code: '',
      name: '',
      level: '',
      location: '',
      subordinateLevelId: '',
      verifyModeIds: [],
      status: 2,
      licenseId: '',
    });
    onClose();
  };

  const licenseOptions = getLicenseOptions();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        {editMode ? <EditIcon /> : <AddIcon />}
        {editMode ? t('licenseUnits.editDialog.title') : t('licenseUnits.addDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, pt: 2 }}>
          <Input
            label={t('licenseUnits.addDialog.code')}
            value={formData.code}
            onChange={handleChange('code')}
            required
            autoFocus
            disabled={editMode}
            id="code"
            name="code"
          />
          <Input
            label={t('licenseUnits.addDialog.name')}
            value={formData.name}
            onChange={handleChange('name')}
            required
            id="name"
            name="name"
          />
          <Select
            label={t('licenseUnits.addDialog.level')}
            value={formData.level}
            onChange={handleChange('level')}
            options={unitLevels}
            required
            emptyMessage={t('licenseUnits.addDialog.noLevels')}
            id="level"
            name="level"
          />
          <Select
            label={t('licenseUnits.addDialog.status')}
            value={formData.status}
            onChange={handleChange('status')}
            options={unitStatuses}
            id="status"
            name="status"
          />
          <Select
            label={t('licenseUnits.addDialog.license')}
            value={formData.licenseId}
            onChange={handleChange('licenseId')}
            options={licenseOptions.map((l) => ({ id: l.id, name: l.code }))}
            emptyMessage={t('licenseUnits.addDialog.noLicenses')}
            id="licenseId"
            name="licenseId"
          />
          <Input
            label={t('licenseUnits.addDialog.location')}
            value={formData.location}
            onChange={handleChange('location')}
            id="location"
            name="location"
          />
          <Select
            label={t('licenseUnits.addDialog.parentUnit')}
            value={formData.subordinateLevelId}
            onChange={handleChange('subordinateLevelId')}
            options={subordinateLevelOptions}
            emptyMessage={t('licenseUnits.addDialog.noParentUnits')}
            id="subordinateLevelId"
            name="subordinateLevelId"
          />
          <Autocomplete
            multiple
            id="verifyModeIds"
            size="small"
            options={verifyModes}
            getOptionLabel={(option) => option.name || ''}
            value={verifyModes.filter((vm) => formData.verifyModeIds.includes(vm.id))}
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                verifyModeIds: newValue.map((vm) => vm.id),
              });
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('licenseUnits.addDialog.verifyModes')}
                placeholder={
                  formData.verifyModeIds.length === 0 ? t('licenseUnits.addDialog.selectVerifyModes') : ''
                }
                variant="outlined"
                size="small"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.name} {...getTagProps({ index })} key={option.id} size="small" />
              ))
            }
            noOptionsText={t('licenseUnits.addDialog.noVerifyModes')}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" id="cancel" name="cancel">
          {t('licenseUnits.addDialog.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={!isFormValid()}
          startIcon={editMode ? <EditIcon /> : <AddIcon />}
          id="confirm"
          name="confirm"
        >
          {editMode ? t('licenseUnits.editDialog.confirm') : t('licenseUnits.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AddLicenseUnitModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  unitLevels: PropTypes.array,
  unitStatuses: PropTypes.array,
  verifyModes: PropTypes.array,
  availableLicenses: PropTypes.array,
  unitData: PropTypes.object,
  editMode: PropTypes.bool,
};

export default AddLicenseUnitModal;
